import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  Utils,
  fetchWithAuth,
  selectStyles,
  ONELINK_MODAL_STEP,
  ONELINK_LINK_STATUS,
  ONELINK_MEDIA_SOURCE,
  ONELINK_REDIRECT_TYPE,
} from "../../utils";
import { buildInitialDraft, draftToSavePayload } from "./oneLinkUtils";

const CUSTOM_REDIRECT = 2;

const mediaSourceOptions = ONELINK_MEDIA_SOURCE.map((m) => ({ value: m.value, label: m.label }));
const statusOptions = ONELINK_LINK_STATUS.map((s) => ({ value: s.value, label: s.label }));
const androidRedirectOptions = ONELINK_REDIRECT_TYPE.map((r) => ({ value: r.value, label: r.androidLabel }));
const iosRedirectOptions = ONELINK_REDIRECT_TYPE.map((r) => ({ value: r.value, label: r.iosLabel }));

export default function OneLinkCreateModal({
  show,
  onHide,
  appUuid,
  editingDraft,
  onSaved,
}) {
  const [draft, setDraft] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [savedUrl, setSavedUrl] = useState("");

  const isEdit = Boolean(editingDraft?.id);
  const steps = ONELINK_MODAL_STEP;
  const stepIndex = steps.findIndex((s) => s.value === step);
  const currentStep = steps[stepIndex] || steps[0];
  const isLastStep = stepIndex >= steps.length - 1;

  useEffect(() => {
    if (!show) {
      setDraft(null);
      setStep(1);
      setSavedUrl("");
      setSaving(false);
      return;
    }
    if (editingDraft) {
      setDraft({ ...editingDraft });
      setSavedUrl(editingDraft.tracking_url || "");
    } else {
      setDraft(buildInitialDraft());
      setSavedUrl("");
    }
    setStep(1);
  }, [show, editingDraft]);

  const update = (patch) => setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleMediaChange = (mediaSourceValue) => {
    update({ mediaSourceValue });
  };

  const validateStep = () => {
    if (currentStep.key !== "general") return true;
    const cName = (draft?.name || "").trim();
    if (!cName) {
      toast.error("Campaign name is required");
      return false;
    }
    if (!draft?.mediaSourceValue) {
      toast.error("Media source is required");
      return false;
    }
    return true;
  };

  const handleBack = () => {
    if (stepIndex <= 0) {
      onHide();
      return;
    }
    setStep(steps[stepIndex - 1].value);
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    if (!isLastStep) {
      setStep(steps[stepIndex + 1].value);
      return;
    }
    await handleSave();
  };

  const handleSave = async () => {
    if (!appUuid || !draft) return;
    if (!validateStep()) return;
    setSaving(true);
    try {
      const payload = draftToSavePayload(draft, appUuid, draft.id);
      const res = await fetchWithAuth(`${Utils.baseurl}save/onelink`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.status) {
        toast.success(json.message || (isEdit ? "Link updated" : "Link created"));
        if (json.data?.tracking_url) setSavedUrl(json.data.tracking_url);
        onSaved?.();
        onHide();
      } else {
        toast.error(json.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <Modal show={show} onHide={onHide} size="md" centered className="engage-create-modal">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit link" : "Create link"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {steps.length > 1 && (
          <ul className="engage-modal-steps">
            {steps.map((s, idx) => {
              const done = idx < stepIndex;
              const active = s.value === step;
              return (
                <li
                  key={s.value}
                  className={`engage-modal-step-pill ${active ? "active" : ""} ${done ? "done" : ""}`}
                >
                  <span className="engage-modal-step-num">{done ? "✓" : idx + 1}</span>
                  {s.label}
                </li>
              );
            })}
          </ul>
        )}

        <div className="engage-modal-form">
          {currentStep.key === "general" && (
            <div className="engage-modal-step">
              <div className="engage-form-group">
                <label>Campaign name <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  placeholder="e.g. Summer_Sale_Google_Ads"
                  value={draft.name || ""}
                  onChange={(e) => update({ name: e.target.value })}
                />
              </div>
              <div className="engage-form-group">
                <label>Media source</label>
                <Select
                  classNamePrefix="ns-select"
                  isSearchable
                  options={mediaSourceOptions}
                  value={mediaSourceOptions.find((o) => o.value === (draft.mediaSourceValue ?? 1)) || mediaSourceOptions[0]}
                  onChange={(opt) => handleMediaChange(opt?.value ?? 1)}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
              <div className="engage-form-row">
                <div className="engage-form-group">
                  <label>Ad group (optional)</label>
                  <input
                    type="text"
                    value={draft.ad_group || ""}
                    onChange={(e) => update({ ad_group: e.target.value })}
                  />
                </div>
                <div className="engage-form-group">
                  <label>Creative (optional)</label>
                  <input
                    type="text"
                    value={draft.creative || ""}
                    onChange={(e) => update({ creative: e.target.value })}
                  />
                </div>
              </div>
              <div className="engage-toggle-row">
                <label className="mb-0">Retargeting</label>
                <input
                  type="checkbox"
                  checked={Boolean(draft.retargeting)}
                  onChange={(e) => update({ retargeting: e.target.checked })}
                />
              </div>
              {isEdit && (
                <div className="engage-form-group">
                  <label>Status</label>
                  <Select
                    classNamePrefix="ns-select"
                    isSearchable={false}
                    options={statusOptions}
                    value={statusOptions.find((o) => o.value === (draft.linkStatusValue ?? 1)) || statusOptions[0]}
                    onChange={(opt) => update({ linkStatusValue: opt?.value ?? 1 })}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              )}
            </div>
          )}
          {currentStep.key === "deeplink" && (
            <div className="engage-modal-step">
              <p className="engage-step-intro">Deep links and fallback URLs when the app is not installed.</p>
              <div className="engage-form-group">
                <label>Deep link value</label>
                <input
                  type="text"
                  placeholder="e.g. product/123"
                  value={draft.deep_link_value || ""}
                  onChange={(e) => update({ deep_link_value: e.target.value })}
                />
              </div>
              <div className="engage-form-group">
                <label>Additional deep link parameters (optional)</label>
                <input
                  type="text"
                  placeholder="key=value&key2=value2"
                  value={draft.deep_link_extra || ""}
                  onChange={(e) => update({ deep_link_extra: e.target.value })}
                />
              </div>
              <div className="engage-form-group">
                <label>URI scheme (optional)</label>
                <input
                  type="text"
                  placeholder="myapp://"
                  value={draft.uri_scheme || ""}
                  onChange={(e) => update({ uri_scheme: e.target.value })}
                />
              </div>
              <h3 className="engage-step-subtitle">Fallback redirects</h3>
              <div className="engage-form-group">
                <label>Android</label>
                <Select
                  classNamePrefix="ns-select"
                  className="mb-2"
                  isSearchable={false}
                  options={androidRedirectOptions}
                  value={androidRedirectOptions.find((o) => o.value === (draft.redirectAndroidValue ?? 1)) || androidRedirectOptions[0]}
                  onChange={(opt) => update({ redirectAndroidValue: opt?.value ?? 1 })}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
                {draft.redirectAndroidValue === CUSTOM_REDIRECT && (
                  <input
                    type="url"
                    placeholder="https://"
                    value={draft.redirect_url_android || ""}
                    onChange={(e) => update({ redirect_url_android: e.target.value })}
                  />
                )}
              </div>
              <div className="engage-form-group">
                <label>iOS</label>
                <Select
                  classNamePrefix="ns-select"
                  className="mb-2"
                  isSearchable={false}
                  options={iosRedirectOptions}
                  value={iosRedirectOptions.find((o) => o.value === (draft.redirectIosValue ?? 1)) || iosRedirectOptions[0]}
                  onChange={(opt) => update({ redirectIosValue: opt?.value ?? 1 })}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
                {draft.redirectIosValue === CUSTOM_REDIRECT && (
                  <input
                    type="url"
                    placeholder="https://"
                    value={draft.redirect_url_ios || ""}
                    onChange={(e) => update({ redirect_url_ios: e.target.value })}
                  />
                )}
              </div>
              <div className="engage-form-group">
                <label>Desktop / web fallback (optional)</label>
                <input
                  type="url"
                  placeholder="https://"
                  value={draft.redirect_url_web || ""}
                  onChange={(e) => update({ redirect_url_web: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        {savedUrl && (
          <div className="engage-saved-url-box mt-3 p-2 border rounded bg-light">
            <small className="text-muted d-block mb-1">Tracking URL (short)</small>
            <code className="text-break">{savedUrl}</code>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={saving}>
          {stepIndex <= 0 ? "Cancel" : "Back"}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleNext} disabled={saving}>
          {saving ? "Saving…" : isLastStep ? (isEdit ? "Save changes" : "Create link") : "Next"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
